import { useContext, useEffect, useRef, useState } from "react";
import { css } from "@emotion/react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  useTheme,
} from "@mui/material";
import { Formik } from "formik";

import type {
  HospitalMhlAutoRouteConfig,
  ManagedLocation,
} from "@thalamos/aspire-common";

import { api, apiHooks } from "../../api.js";
import { ManagedLocationsContext } from "../../Contexts.js";
import { breakpoints } from "../../styles/themeCommon.js";
import { ConfirmationModal } from "../ConfirmationModal.js";
import { useAssessingOrganisationsApi } from "./DrFinderAssessingOrganisationsPicker.js";
import {
  ManagedLocationEditForm,
  type ManagedLocationEditFormProps,
} from "./ManagedLocationEditForm.js";
import {
  isEditableLocation,
  managedLocationSchema,
  type ManagedLocationFormData,
} from "./managedLocationForm.js";
import { ManagedLocationPicker } from "./ManagedLocationPicker.js";
import {
  Button,
  ButtonIcon,
  MenuFlyout,
  PopupDialog,
  renderErrorToast,
  renderSuccessToast,
  Typeahead,
} from "~/components/design-system/index.js";
import {
  DropdownFormField,
  NameAddressFormField,
  TextboxFormField,
} from "~/components/form/index.js";
import { createFuse } from "~/lib/compat/fuse.js";

export function ListsManagementPage() {
  const theme = useTheme();
  const managedLocationsContext = useContext(ManagedLocationsContext);

  const editableLocations =
    managedLocationsContext.managedLocations.filter(isEditableLocation);

  const managedLocationSearch = createFuse(editableLocations, {
    keys: ["name", "address", "postalCode", "odsCode"],
    threshold: 0.3,
  });

  const [selectedItem, setSelectedItem] = useState<ManagedLocation | null>(
    null,
  );

  // Supervisory bodies are themselves managed locations, so the options come from
  // the same curated list the page is editing.
  const localAuthorities = managedLocationsContext.managedLocations.filter(
    (location) =>
      location.type === "localAuthority" && location.status === "active",
  );

  const [supervisoryLaQuery, setSupervisoryLaQuery] = useState<string>("");

  const [trustQuery, setTrustQuery] = useState<string>(
    typeof selectedItem?.trustName === "string" ? selectedItem.trustName : "",
  );

  // MHL auto-route config state for hospitals
  const [mhlAutoRouteConfig, setMhlAutoRouteConfig] =
    useState<HospitalMhlAutoRouteConfig | null>(null);

  useEffect(() => {
    if (selectedItem && selectedItem.trustName) {
      setTrustQuery(selectedItem.trustName);
    } else {
      setTrustQuery("");
    }
    // Initialize MHL config when selecting a hospital
    if (selectedItem?.type === "hospital") {
      setMhlAutoRouteConfig(selectedItem.mhlAutoRouteConfig ?? null);
    } else {
      setMhlAutoRouteConfig(null);
    }
  }, [selectedItem]);

  const [{ data: trustResults }] = apiHooks.trust.search(trustQuery, 10, 0);

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);

  const results: Array<{ item: ManagedLocation }> = query
    ? managedLocationSearch.search(query)
    : editableLocations.map((ml) => ({ item: ml }));

  const numPages = results.length ? Math.ceil(results.length / 10) : 1;

  return (
    <Box>
      <PopupDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
      >
        <Formik<ManagedLocationFormData>
          validationSchema={managedLocationSchema}
          onSubmit={async (values, { setSubmitting }) => {
            setSubmitting(true);
            const result = await api.configuration.addAdminManagedLocation({
              type: values.type!,
              name: values.name.trim(),
              address: values.address.address,
              postalCode: values.address.postalCode,
              odsCode: values.odsCode ? values.odsCode : undefined,
              trustId: values.trustId ?? undefined,
              xeroInvoicePrefix: values.xeroInvoicePrefix || undefined,
              cqcLocationId: values.cqcLocationId || undefined,
              supervisoryLocalAuthorityId:
                values.supervisoryLocalAuthorityId ?? undefined,
              icbId:
                values.type === "localAuthority"
                  ? (values.icbId ?? undefined)
                  : undefined,
            });

            if (result.status === 204) {
              renderSuccessToast({ message: "Managed location added" });
              managedLocationsContext.refetchManagedLocations();
              setShowCreateDialog(false);
            } else if (result.status === 409) {
              // @ts-expect-error the client types this endpoint's response body as
              // carrying no value whatever the status, so the reason returned with a
              // 409 is invisible to the types.
              renderErrorToast({ message: result.data.reason });
            } else {
              renderErrorToast({ message: "Failed to add managed location" });
            }
            setSubmitting(false);
          }}
          initialValues={
            {
              type: null,
              name: "",
              address: { address: "", postalCode: "", isConfirmed: false },
              odsCode: "",
              trustId: undefined,
              xeroInvoicePrefix: null,
            } as ManagedLocationFormData
          }
        >
          {(formikData) => {
            const {
              values,
              setValues,
              setFieldTouched,
              errors,
              touched,
              handleBlur,
              submitForm,
            } = formikData;

            const fieldProps = {
              validationSchema: managedLocationSchema,
              values,
              setValues,
              setFieldTouched,
              errors,
              touched,
              context: {},
              handleBlur,
            };

            return (
              <>
                <Box sx={{ mb: 1 }}>
                  <DropdownFormField
                    field={{
                      type: "dropdown",
                      label: "Type (*)",
                      valueField: "type",
                      textField: "typeLabel",
                      options: [
                        { label: "Hospital", value: "hospital" },
                        { label: "Local Authority", value: "localAuthority" },
                        { label: "Care Home", value: "careHome" },
                      ],
                    }}
                    fieldProps={fieldProps}
                  />
                </Box>
                <Box sx={{ mb: 0 }}>
                  <TextboxFormField
                    field={{
                      type: "textbox",
                      label: "Name (*)",
                      field: "name",
                    }}
                    fieldProps={fieldProps}
                  />
                </Box>
                <Box sx={{ mb: 1 }}>
                  <NameAddressFormField
                    field={{
                      type: "name-address",
                      disableName: true,
                      field: "address",
                      addressLabel: "Address (*)",
                    }}
                    fieldProps={fieldProps}
                  />
                </Box>
                <Box sx={{ mb: 1 }}>
                  <Typeahead
                    disabled={false}
                    inputValue={trustQuery}
                    onInputChange={(e) => {
                      const value = typeof e === "string" ? e : "";
                      setTrustQuery(value); // local input display
                      const match = (trustResults?.results ?? []).find(
                        (t) => t.name === value,
                      );
                      setValues({
                        ...values,
                        trustId: match ? match.id : undefined,
                      }); // only set trustId in Formik
                    }}
                    options={(trustResults?.results ?? []).map((t) => ({
                      label: t.name,
                      value: t.id,
                    }))}
                    name="trust"
                    label="Trust (NEW OPTIONAL)"
                    errorMessage={
                      errors.trustId && touched.trustId
                        ? errors.trustId
                        : undefined
                    }
                  />
                </Box>
                <Box sx={{ mb: 1 }}>
                  <TextboxFormField
                    field={{
                      type: "textbox",
                      label: "ODS Code (optional)",
                      field: "odsCode",
                    }}
                    fieldProps={fieldProps}
                  />
                </Box>
                {values.type === "localAuthority" && (
                  <Box sx={{ mb: 1 }}>
                    <TextboxFormField
                      field={{
                        type: "textbox",
                        label:
                          "Xero Invoice Prefix (optional, defaults to DrFinder)",
                        field: "xeroInvoicePrefix",
                      }}
                      fieldProps={fieldProps}
                    />
                  </Box>
                )}
                {values.type === "careHome" && (
                  <>
                    <Box sx={{ mb: 1 }}>
                      <TextboxFormField
                        field={{
                          type: "textbox",
                          label: "CQC Location ID (optional)",
                          field: "cqcLocationId",
                        }}
                        fieldProps={fieldProps}
                      />
                    </Box>
                    <Box sx={{ mb: 1 }}>
                      <Typeahead
                        disabled={false}
                        inputValue={supervisoryLaQuery}
                        onInputChange={(e) => {
                          const value = typeof e === "string" ? e : "";
                          setSupervisoryLaQuery(value);
                          const match = localAuthorities.find(
                            (la) => la.name === value,
                          );
                          setValues({
                            ...values,
                            supervisoryLocalAuthorityId: match
                              ? match.id
                              : null,
                          });
                        }}
                        options={localAuthorities.map((la) => ({
                          label: la.name,
                          value: la.id,
                        }))}
                        name="supervisoryLocalAuthority"
                        label="Supervisory Local Authority (grants DoLS authorisations)"
                        errorMessage={
                          errors.supervisoryLocalAuthorityId &&
                          touched.supervisoryLocalAuthorityId
                            ? errors.supervisoryLocalAuthorityId
                            : undefined
                        }
                      />
                    </Box>
                  </>
                )}
                {values.type === "localAuthority" && (
                  <ManagedLocationPicker
                    locationType="icb"
                    label="ICB (sets the out-of-area payment policy)"
                    error={errors.icbId && touched.icbId ? errors.icbId : null}
                    idField={"icbId"}
                    values={values}
                    setValues={setValues}
                  />
                )}
                <Button label={"Create"} onClick={submitForm} />
              </>
            );
          }}
        </Formik>
      </PopupDialog>

      <PopupDialog
        open={showEditDialog}
        onClose={() => setShowEditDialog(false)}
      >
        {selectedItem && (
          <ConnectedManagedLocationEditForm
            location={selectedItem}
            trust={{
              query: trustQuery,
              onQueryChange: setTrustQuery,
              options: (trustResults?.results ?? []).map((t) => ({
                label: t.name,
                value: t.id,
              })),
            }}
            supervisoryLocalAuthority={{
              query: supervisoryLaQuery,
              onQueryChange: setSupervisoryLaQuery,
              options: localAuthorities.map((la) => ({
                label: la.name,
                value: la.id,
              })),
            }}
            mhlAutoRouteConfig={mhlAutoRouteConfig}
            onMhlAutoRouteConfigChange={setMhlAutoRouteConfig}
            onLocationSaved={() => {
              managedLocationsContext.refetchManagedLocations();
            }}
            onClose={() => {
              setShowEditDialog(false);
            }}
          />
        )}
      </PopupDialog>

      <Box
        sx={{
          display: "flex",
          width: "100%",
          marginTop: "1em",
          marginBottom: "1em",
        }}
      >
        <TextField
          placeholder={"Type to filter..."}
          sx={{ width: "100%" }}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPage(0);
          }}
        />
        <Box
          css={css`
            @media (max-width: ${breakpoints.values.sm}px) {
              margin-left: ${theme.spacing(2)};
            }

            display: flex;
            margin-bottom: ${theme.spacing(2)};
            margin-left: ${theme.spacing(2)};
          `}
        >
          <Button
            variant={"contained"}
            label={"Create"}
            endIcon={ButtonIcon.add}
            onClick={() => setShowCreateDialog(true)}
          />
        </Box>
      </Box>
      <Box>
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <button onClick={() => page > 0 && setPage(page - 1)}>&#60;</button>
          <Box sx={{ marginLeft: "1em", marginRight: "1em" }}>
            Page {page + 1} of {numPages}
          </Box>
          <button onClick={() => page < numPages - 1 && setPage(page + 1)}>
            &#62;
          </button>
        </Box>
        <TableContainer>
          <Table size="small" aria-label="a dense table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Address</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Postcode</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Trust</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>ODS Code</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results &&
                results
                  .slice(page * 10, page * 10 + 10)
                  .map((r: any) => (
                    <ListManagementItems
                      setShowEditDialog={setShowEditDialog}
                      setSelectedItem={setSelectedItem}
                      resultItem={r}
                      refetchManagedLocations={
                        managedLocationsContext.refetchManagedLocations
                      }
                    />
                  ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}

export function ListManagementItems({
  resultItem,
  setShowEditDialog,
  setSelectedItem,
  refetchManagedLocations,
}: {
  resultItem: { item: ManagedLocation };
  setShowEditDialog: (value: boolean) => void;
  setSelectedItem: (item: ManagedLocation) => void;
  refetchManagedLocations: () => void;
}) {
  const cardRef = useRef(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [confirmFn, setConfirmFn] = useState<{
    confirmFn: () => void;
    message: string;
  } | null>(null);

  const options = [
    {
      name: "Edit",
      onClick: () => {
        setSelectedItem(resultItem.item);
        setShowEditDialog(true);
      },
      icon: "edit",
      disabled: false,
    },
    {
      name: "Delete",
      onClick: () => {
        setConfirmFn({
          confirmFn: async () => {
            const result = await api.configuration.deleteAdminManagedLocation(
              resultItem.item.id,
            );

            if (result.status === 204) {
              renderSuccessToast({
                message: "Successfully deleted location.",
              });
              refetchManagedLocations();
            } else {
              renderErrorToast({
                message:
                  typeof result.data === "object"
                    ? result.data.reason
                    : "Failed to delete location.",
              });
            }

            setConfirmFn(null);
          },
          message: `Are you sure you want to delete ${resultItem.item.name}?`,
        });
      },
      icon: "delete",
      disabled: false,
    },
  ];

  return (
    <>
      {confirmFn && (
        <ConfirmationModal
          message={confirmFn.message}
          confirmFn={confirmFn.confirmFn}
          closeFn={() => setConfirmFn(null)}
        />
      )}
      <TableRow key={resultItem.item.name}>
        <TableCell>
          {resultItem.item.type === "localAuthority"
            ? "LA"
            : resultItem.item.type === "careHome"
              ? "Care Home"
              : "Hospital"}
        </TableCell>
        <TableCell>{resultItem.item.name}</TableCell>
        <TableCell>{resultItem.item.address}</TableCell>
        <TableCell>{resultItem.item.postalCode}</TableCell>
        <TableCell>{resultItem.item.trustName}</TableCell>
        <TableCell>{resultItem.item.odsCode}</TableCell>
        <TableCell>
          <Box ref={cardRef} onClick={() => setIsMenuOpen(true)}>
            <MenuFlyout
              cardRef={cardRef}
              options={options}
              isOpen={isMenuOpen}
              onClose={() => setIsMenuOpen(false)}
            />
          </Box>
        </TableCell>
      </TableRow>
    </>
  );
}

/** Saves an edited location's details, and a hospital's MHL auto-routing. */
async function saveLocation(
  location: ManagedLocation,
  values: ManagedLocationFormData,
  mhlAutoRouteConfig: HospitalMhlAutoRouteConfig | null,
): Promise<string | null> {
  if (values.type === null) {
    return "Type is required";
  }

  const result = await api.configuration.updateAdminManagedLocation(
    location.id,
    {
      type: values.type,
      name: values.name.trim(),
      address: values.address.address,
      postalCode: values.address.postalCode,
      odsCode: values.odsCode ? values.odsCode : undefined,
      trustId: values.trustId ?? null,
      xeroInvoicePrefix: values.xeroInvoicePrefix || null,
      cqcLocationId: values.cqcLocationId || null,
      supervisoryLocalAuthorityId: values.supervisoryLocalAuthorityId ?? null,
      icbId:
        values.type === "localAuthority" ? (values.icbId ?? null) : undefined,
    },
  );

  if (result.status === 409 || result.status === 400) {
    // An ODS code the endpoint rejects arrives as a conflict and a link it
    // refuses as a bad request; both carry the reason, and a generic message
    // would discard it. The client types the body as carrying no value whatever
    // the status, so the reason is checked for at runtime.
    const body: unknown = result.data;
    if (
      typeof body === "object" &&
      body !== null &&
      "reason" in body &&
      typeof body.reason === "string"
    ) {
      return body.reason;
    }
  }

  if (result.status !== 204) {
    return "Failed to update managed location";
  }

  if (values.type === "hospital" && mhlAutoRouteConfig) {
    const mhlResult = await api.configuration.updateHospitalMhlAutoRouteConfig(
      location.id,
      mhlAutoRouteConfig,
    );
    if (mhlResult.status !== 200) {
      return "Failed to update MHL auto-route configuration";
    }
  }

  return null;
}

/** {@link ManagedLocationEditForm} wired to the API. */
function ConnectedManagedLocationEditForm(
  props: Omit<
    ManagedLocationEditFormProps,
    "saveLocation" | "assessingOrganisations"
  >,
) {
  const assessingOrganisations = useAssessingOrganisationsApi(
    props.location.id,
  );

  return (
    <ManagedLocationEditForm
      {...props}
      saveLocation={(values) =>
        saveLocation(props.location, values, props.mhlAutoRouteConfig)
      }
      assessingOrganisations={assessingOrganisations}
    />
  );
}
